import { app } from '../../app';
import { arrayRandom } from '../../../lib/util/jsTools';
import { ColorId } from '../../defs/block';
import { CollideEvent, CollideId, isOneHitCollider } from '../../defs/collide';
import type { SirenBlockEntity } from '../../entities/BlockEntity';
import { despawnBlockEntity } from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { mapGetPan } from '../../util/mapTools';
import { InvalidEffect } from '../effects/InvalidEffect';
import { CollideHandler, ICollision } from './ICollision';
import { SirenBlockEffect } from '../effects/SirenBlockEffect';

/*
    siren block collision resolver
*/
export class SirenCollision implements ICollision {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entity: SirenBlockEntity;
  // map
  private readonly _resolvers: { [key in CollideId]?: CollideHandler } = {
    tap: this._resolveTap,
    block: this._resolveBlock,
    attack: this._resolveAttack,
  };

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, entity: SirenBlockEntity) {
    this._scene = scene;
    this._entity = entity;
  }

  // impl
  //-------------------------------------------------------------------------
  public async resolve(event: CollideEvent): Promise<boolean> {
    return this._resolvers[event.id]?.call(this, event) ?? false;
  }

  // private: resolvers
  //-------------------------------------------------------------------------
  private async _resolveTap(event: CollideEvent) {
    // invalid effect
    await new InvalidEffect(this._entity).execute();
    return false;
  }

  private async _resolveBlock(event: CollideEvent) {
    const collider = event.collider;

    // if block collider
    if (collider?.type === 'block') {
      const colliderProps = collider.entity.c.block.props;

      // if block collider is basic block, poke siren with its color
      if (colliderProps.type === 'basic') {
        const success = await this._poke(colliderProps.color);
        return success;
      }
    }
    return false;
  }

  private getRandomColorToPoke() {
    // choose random color among existing sirens
    const colorId = arrayRandom(
      this._entity.c.blockSiren.view.sirens
        .filter((x) => !!x)
        .map((x) => x!.color),
    ) as ColorId;
    return colorId;
  }

  private async _resolveAttack(event: CollideEvent) {
    if (isOneHitCollider(event.collider?.type)) {
      // To destroy in one hit, poke it X times according to the remaining hits
      // So the poke animation (gummy bear flying out the box) can play multiple times
      const hitsToDestroy = this._entity.c.blockSiren.view.sirens.length;
      for (let i = 0; i < hitsToDestroy; i++) {
        const colorId = this.getRandomColorToPoke();
        this._poke(colorId);
      }
      return true;
    }
    // choose random color among existing sirens
    const colorId = this.getRandomColorToPoke();
    // poke that color
    this._poke(colorId);
    return true;
  }

  // private: actions
  //-------------------------------------------------------------------------
  private async _poke(colorId: ColorId) {
    const c = this._entity.c;
    const component = c.blockSiren;
    const position = c.position.mapPosition;
    const effect = new SirenBlockEffect(this._scene, { entity: this._entity });
    const sirenSprite = component.view.getSirenSprite(colorId);

    // poke siren component with color of collider
    const success = component.poke(colorId);

    // Only play sound and execute break effect if a colour was removed
    if (success) {
      if (sirenSprite) {
        effect.animateSirenBreak(sirenSprite);
      }

      // if siren block empty, despawn it
      if (component.isEmpty()) {
        effect.animateBoxBreak();
        despawnBlockEntity(this._scene, this._entity);
      } else {
        this._scene?.events.publish({ id: 'poke' });
      }

      // play sound
      app.sound.play('puzzle-siren-destroy', {
        pan: mapGetPan(this._entity.c.position.mapPosition),
      });
    }

    return success;
  }
}
